import { useMutation, useQuery } from "@apollo/client";
import { useMarketingWorkspaceConfig } from "../config";
import {
  ACCEPT_WORKSPACE_INVITATION,
  ACCEPT_WORKSPACE_INVITATION_BY_ID,
  GET_MY_PENDING_INVITATIONS,
  GET_WORKSPACE_MEMBERS,
  GET_WORKSPACE_PENDING_INVITATIONS,
  INVITE_WORKSPACE_MEMBER,
  RESEND_WORKSPACE_INVITATION,
  REVOKE_WORKSPACE_INVITATION,
} from "../graphql/memberDocuments";
import { GET_MY_MARKETING_WORKSPACES } from "../graphql/workspaceDocuments";
import type {
  IInviteMarketingWorkspaceMemberInput,
  IMarketingWorkspaceInvitation,
  IMarketingWorkspaceMember,
} from "../types";

export function useWorkspacePendingInvitations(workspaceId?: string) {
  const { getClient } = useMarketingWorkspaceConfig();

  const { data, loading, error, refetch } = useQuery(GET_WORKSPACE_PENDING_INVITATIONS, {
    client: getClient(),
    variables: { workspaceId },
    skip: !workspaceId,
    fetchPolicy: "cache-and-network",
  });

  return {
    invitations: (data?.marketingWorkspacePendingInvitations?.items ??
      []) as IMarketingWorkspaceInvitation[],
    isLoading: loading,
    error,
    refetch,
  };
}

export function useMyPendingInvitations(skip = false) {
  const { getClient } = useMarketingWorkspaceConfig();

  const { data, loading, error, refetch } = useQuery(GET_MY_PENDING_INVITATIONS, {
    client: getClient(),
    skip,
    fetchPolicy: "cache-and-network",
  });

  return {
    invitations: (data?.myMarketingWorkspaceInvitations ?? []) as IMarketingWorkspaceInvitation[],
    hasLoaded: Boolean(data),
    isLoading: loading,
    error,
    refetch,
  };
}

export function useInviteWorkspaceMember() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation(INVITE_WORKSPACE_MEMBER, {
    client: getClient(),
    refetchQueries: [GET_WORKSPACE_PENDING_INVITATIONS, GET_WORKSPACE_MEMBERS],
    awaitRefetchQueries: true,
  });

  const inviteMember = async (
    workspaceId: string,
    input: IInviteMarketingWorkspaceMemberInput
  ) => {
    const result = await mutate({ variables: { workspaceId, request: input } });
    return result.data?.inviteMarketingWorkspaceMember as IMarketingWorkspaceInvitation;
  };

  return { inviteMember, isLoading: loading, error };
}

export function useAcceptWorkspaceInvitation() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation(ACCEPT_WORKSPACE_INVITATION, {
    client: getClient(),
  });

  const acceptInvitation = async (token: string) => {
    const result = await mutate({ variables: { token } });
    return result.data?.acceptMarketingWorkspaceInvitation as IMarketingWorkspaceMember;
  };

  return { acceptInvitation, isLoading: loading, error };
}

export function useAcceptWorkspaceInvitationById() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation(ACCEPT_WORKSPACE_INVITATION_BY_ID, {
    client: getClient(),
    refetchQueries: [GET_MY_MARKETING_WORKSPACES, GET_MY_PENDING_INVITATIONS],
    awaitRefetchQueries: true,
  });

  const acceptInvitation = async (invitationId: string) => {
    const result = await mutate({ variables: { invitationId } });
    return result.data?.acceptMarketingWorkspaceInvitationById as IMarketingWorkspaceMember;
  };

  return { acceptInvitation, isLoading: loading, error };
}

export function useResendWorkspaceInvitation() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation(RESEND_WORKSPACE_INVITATION, {
    client: getClient(),
    refetchQueries: [GET_WORKSPACE_PENDING_INVITATIONS],
    awaitRefetchQueries: true,
  });

  const resendInvitation = async (workspaceId: string, invitationId: string) => {
    const result = await mutate({ variables: { workspaceId, invitationId } });
    return result.data?.resendMarketingWorkspaceInvitation as IMarketingWorkspaceInvitation;
  };

  return { resendInvitation, isLoading: loading, error };
}

export function useRevokeWorkspaceInvitation() {
  const { getClient } = useMarketingWorkspaceConfig();

  const [mutate, { loading, error }] = useMutation(REVOKE_WORKSPACE_INVITATION, {
    client: getClient(),
    refetchQueries: [GET_WORKSPACE_PENDING_INVITATIONS],
    awaitRefetchQueries: true,
  });

  const revokeInvitation = async (workspaceId: string, invitationId: string) => {
    const result = await mutate({ variables: { workspaceId, invitationId } });
    return Boolean(result.data?.revokeMarketingWorkspaceInvitation);
  };

  return { revokeInvitation, isLoading: loading, error };
}
